import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../styles/ContextBadge.css";

interface ContextBadgeProps {
  context: string;
  variant?: "creative" | "professional";
}

const ContextBadge: React.FC<ContextBadgeProps> = ({
  context,
  variant = "creative",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<"above" | "below">("above");
  const badgeRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Determine if popover should appear above or below based on viewport position
  useEffect(() => {
    if (isOpen && badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;

      // If there's more space below or not enough space above, show below
      if (spaceBelow > 200 || spaceAbove < 150) {
        setPosition("below");
      } else {
        setPosition("above");
      }
    }
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        badgeRef.current &&
        !badgeRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const popoverVariants = {
    hidden: {
      opacity: 0,
      y: position === "above" ? 8 : -8,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring" as const,
        stiffness: 300,
        damping: 25,
      },
    },
    exit: {
      opacity: 0,
      y: position === "above" ? 8 : -8,
      scale: 0.95,
      transition: {
        duration: 0.15,
      },
    },
  };

  return (
    <div className={`context-badge-wrapper ${variant}`}>
      <button
        ref={badgeRef}
        className={`context-badge ${variant} ${isOpen ? "active" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        aria-label="Show additional context"
        aria-expanded={isOpen}
      >
        {variant === "creative" ? (
          // Whimsical quill/scroll icon for creative
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="badge-icon"
          >
            <path d="M20 2c-2 2-6 4-9 4s-5 2-5 5c0 2 1 4 3 5l1 1c1 2 3 3 5 3 3 0 5-2 5-5 0-3-2-5-4-6l-1-1" />
            <path d="M4 22l3-3" />
            <path d="M7 19l-3 3" />
            <circle cx="9" cy="15" r="1" fill="currentColor" />
          </svg>
        ) : (
          // Clean info icon for professional
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="badge-icon"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={popoverRef}
            className={`context-popover ${variant} ${position}`}
            variants={popoverVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
          >
            <div className="popover-content">
              <p>{context}</p>
            </div>
            <div className={`popover-arrow ${position}`} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContextBadge;
